import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from './components/Sidebar';
import PdfViewer from './components/PdfViewer';
import FileList from './components/FileList';
import Header from './components/Header';
import Footer from './components/Footer';
import FrontPage from './components/FrontPage';
import OrganizationFrontPage from './components/OrganizationFrontPage';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { findFolderById, findFolderPath, createFolderLookupMaps } from './utilities/folderUtils';
import { filterFoldersByOrganization, getOrganizationFromPath } from './utilities/organizationUtils';

const App = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [parentFolderId, setParentFolderId] = useState(null);
  const [folderFiles, setFolderFiles] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [isFileListVisible, setIsFileListVisible] = useState(false);
  const [isFrontPageVisible, setIsFrontPageVisible] = useState(true);
  const [topLevelFolders, setTopLevelFolders] = useState([]);
  const [fileListLoading, setFileListLoading] = useState(false);
  const [allFolders, setAllFolders] = useState(null);
  const [folderLookupMaps, setFolderLookupMaps] = useState(null);
  const [filteredFolders, setFilteredFolders] = useState(null);
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [selectedFolderName, setSelectedFolderName] = useState('');

  const { id: folderIdFromUrl, id: fileIdFromUrl, orgKey } = useParams();
  const location = useLocation();
  const pathname = location.pathname;
  const isFileRoute = pathname.includes('/file/');
  const isFolderRoute = pathname.includes('/folder/');
  const isOrgRoute = pathname.includes('/org/');
  const isOrgRootRoute = isOrgRoute && !isFolderRoute && !isFileRoute;
  const navigate = useNavigate();

  // Set initial visibility based on route type
  useEffect(() => {
    if (isFileRoute) {
      setIsFrontPageVisible(false);
      setIsFileListVisible(false);
    } else if (isFolderRoute) {
      setIsFrontPageVisible(false);
      setIsFileListVisible(true);
    } else if (isOrgRootRoute) {
      setIsFrontPageVisible(false);
      setIsFileListVisible(false);
    } else {
      setIsFrontPageVisible(true);
      setIsFileListVisible(false);
    }
  }, [isFileRoute, isFolderRoute, isOrgRootRoute]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_SHAREPOINT_FUNCTION_URL}`)
      .then(response => response.json())
      .then(data => {
        const topFolders = data.Children.filter(item => item.Children.length > 0);
        setTopLevelFolders(topFolders);
        setAllFolders(data);
        
        const lookupMaps = createFolderLookupMaps(data);
        setFolderLookupMaps(lookupMaps);
      })
      .catch(error => {
        console.error('Error fetching top-level folders:', error);
      });
  }, []);

  useEffect(() => {
    const orgFromPath = getOrganizationFromPath(pathname);
    setCurrentOrganization(orgFromPath);
    
    if (allFolders) {
      if (orgFromPath) {
        const filtered = filterFoldersByOrganization(allFolders, orgFromPath);
        setFilteredFolders(filtered);
      } else {
        setFilteredFolders(allFolders);
      }
    }
  }, [pathname, allFolders]);

  const selectFolder = useCallback((folderId, folderUrl, folderName) => {
    // Prevent re-fetching if we're already showing this folder's content
    if (selectedFolderId === folderId && !fileListLoading) {
      return;
    }

    if (fileListLoading) {
      return;
    }

    setSelectedFolderId(folderId);
    setSelectedFolderName(folderName || '');
    setSelectedFile(null);
    setIsFileListVisible(true);
    setIsFrontPageVisible(false);
    setSearchResults([]);

    if (folderUrl) {
      setFileListLoading(true);
      fetch(`${process.env.REACT_APP_SHAREPOINT_FILE_FUNCTION_URL}?folderUrl=${encodeURIComponent(folderUrl)}`)
        .then(response => response.json())
        .then(data => {
          setFolderFiles(data || []);
          setFileListLoading(false);
        })
        .catch(error => {
          console.error('Error fetching folder files:', error);
          setFolderFiles([]);
          setFileListLoading(false);
        });
    } else {
      setFolderFiles([]);
      setFileListLoading(false);
    }
  }, [selectedFolderId, fileListLoading]);

  useEffect(() => {
    if (isFileRoute && fileIdFromUrl && !selectedFile && allFolders && folderLookupMaps) {
      const fetchFileAndParentFolder = async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_SHAREPOINT_FILE_CONTENT_FUNCTION_URL}?itemId=${fileIdFromUrl}&includeParent=true`);
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          
          const fileData = await response.json();
          
          const file = {
            Id: fileIdFromUrl,
            Name: fileData.name || fileData.Name || `File ${fileIdFromUrl}`,
            Historisk: fileData.historisk || fileData.Historisk || "False",
            base64Content: fileData.base64Content,
            _pdfContentFetched: true,
            ...fileData
          };
          
          setSelectedFile(file);
          
          if (fileData.parentReferenceId && !parentFolderId) {
            setParentFolderId(fileData.parentReferenceId);
          } else if (parentFolderId) {
            // Keep existing parent folder ID
          } else {
            setParentFolderId(null);
          }
          
        } catch (error) {
          console.error('Error fetching file:', error);
          navigate('/');
        }
      };
      
      fetchFileAndParentFolder();
    }
  }, [fileIdFromUrl, isFileRoute, selectedFile, allFolders, folderLookupMaps, parentFolderId, navigate]);

  useEffect(() => {
    if (isFolderRoute && folderIdFromUrl) {
      if (selectedFile) {
        setSelectedFile(null);
      }
      
      if (filteredFolders && (selectedFolderId !== folderIdFromUrl || folderFiles.length === 0)) {
        const folder = findFolderById(filteredFolders, folderIdFromUrl);
        if (folder) {
          selectFolder(folderIdFromUrl, folder.FileUrl, folder.Name);
        } else {
          console.warn(`Folder with ID ${folderIdFromUrl} not found.`);
          const redirectPath = currentOrganization ? `/org/${currentOrganization}` : '/';
          navigate(redirectPath);
        }
      }
    } else if (isOrgRootRoute) {
      setSelectedFolderId(null);
      setParentFolderId(null);
      setFolderFiles([]);
      setSelectedFile(null);
      setSelectedFolderName('');
    } else if (!isFolderRoute && !isFileRoute && !isOrgRoute) {
      setSelectedFolderId(null);
      setParentFolderId(null);
      setFolderFiles([]);
      setSelectedFile(null);
      setSelectedFolderName('');
    }
  }, [folderIdFromUrl, isFolderRoute, isFileRoute, isOrgRootRoute, filteredFolders, currentOrganization, navigate, selectedFolderId, selectedFile, folderFiles.length, selectFolder]);

  const handleFileSelect = (file) => {
    setSelectedFile(file);
    setIsFileListVisible(false);
    setIsFrontPageVisible(false);
    setSearchResults([]);
    
    if (selectedFolderId) {
      setParentFolderId(selectedFolderId);
    }
    
    const filePath = currentOrganization ? `/org/${currentOrganization}/file/${file.Id}` : `/file/${file.Id}`;
    navigate(filePath);
  };

  const handleFolderSelect = (folderId, folderUrl, folderName) => {
    selectFolder(folderId, folderUrl, folderName);
    
    if (currentOrganization) {
      navigate(`/org/${currentOrganization}/folder/${folderId}`);
    } else {
      navigate(`/folder/${folderId}`);
    }
  };

  const handleClearSearch = () => {
    setSearchResults([]);
  };

  return (
    <div className="flex flex-col h-screen">
      <Header 
        onFileSelect={handleFileSelect} 
        onSearch={setSearchResults} 
        folderStructure={allFolders}
      />
      <div className="flex flex-1">
        <Sidebar
          onFileSelect={handleFileSelect}
          setSearchFiles={() => { }}
          onFolderSelect={handleFolderSelect}
          onClearSearch={handleClearSearch}
          folderIdFromUrl={isFolderRoute ? folderIdFromUrl : (isFileRoute ? parentFolderId : null)}
          allFolders={filteredFolders}
        />
        <div className="flex-1 flex flex-col">
          {searchResults.length > 0 ? (
            <FileList 
              files={searchResults} 
              onFileSelect={handleFileSelect} 
              onFolderSelect={handleFolderSelect}
              loading={false} 
              isSearchResult={true} 
            />
          ) : isFrontPageVisible ? (
            <FrontPage topLevelFolders={topLevelFolders} />
          ) : isOrgRootRoute ? (
            <OrganizationFrontPage organizationKey={orgKey} />
          ) : isFileListVisible && selectedFolderId ? (
            <FileList 
              files={folderFiles} 
              onFileSelect={handleFileSelect} 
              onFolderSelect={handleFolderSelect}
              loading={fileListLoading} 
              folderName={selectedFolderName} 
            />
          ) : selectedFile ? null : (
            <div className="flex-1 p-6 flex items-center justify-center text-gray-500">
              Please select a folder from the side menu.
            </div>
          )}
          {selectedFile && searchResults.length === 0 && <PdfViewer selectedFile={selectedFile} />}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default App;